import React, { useState, useEffect, useContext } from "react";
import { Button, Row, Col, Card, Alert, Form, Collapse } from "react-bootstrap";
import { abpnClient } from "../../Services/AbpnClient";
import { AuthContext } from "../Auth/AuthProvider";
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import { deepCopy } from "../Shared/Utility/DeepCopy";
import PhysicianDemographicModel, { EthnicityType, GenderType } from './Models/PhysicianDemographicModel';

interface IProps{
    isSectionOpen: boolean;
    physicianId: number;
}

export default function PhysicianDemographicCard(props: IProps) {
    const [openCollapse, setOpenCollapse] = useState<boolean>(props.isSectionOpen);
    const [demographicModel, setDemographicModel] = useState<PhysicianDemographicModel>(null);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [showDeleteSuccess, setShowDeleteSuccess] = useState<boolean>(false);
    const authContext = useContext(AuthContext);
    
    useEffect(() => {
        let url = '/api/physicianDemographic/GetPhysicianDemographic/';
        abpnClient.get<PhysicianDemographicModel>(url, authContext).then(_ => {
            if(_ == null){
                _ = new PhysicianDemographicModel();
                _.personKey = props.physicianId;
            }
            setDemographicModel(_);
        });
    }, []);

    const handleDemographicEthnicityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        var updatedModel = deepCopy(demographicModel);
        updatedModel.ethnicity = Number.parseInt(e.target.id);
        setDemographicModel(updatedModel);
    };

    const handleDemographicGenderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        var updatedModel = deepCopy(demographicModel);
        updatedModel.gender = Number.parseInt(e.target.id);
        setDemographicModel(updatedModel);
    };

    const handleDemographicRaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        var updatedModel = deepCopy(demographicModel);
        const { value, checked } = e.target;
        if(checked){
            updatedModel.races.push({
                 "physicianDemographicRaceId" : null,
                 "physicianDemographicId" : updatedModel.physicianDemographicId,
                 "race" : Number.parseInt(value)
            });
        }
        else{
            updatedModel.races = updatedModel.races.filter((e => e.race.toString() !== value));
        }

        setDemographicModel(updatedModel);
    };

    function isDemographicRaceChecked(raceId: string) : boolean
    {
        if(demographicModel.races == null)
            return false;
        else{
            var isChecked = false;
            demographicModel.races.map((race) => {
                if(race != null && race.race == Number.parseInt(raceId))
                    isChecked = true;
            });
            return isChecked;
        }
    }

    const savePhysicianDemographic = () => {
        let url = '/api/physicianDemographic/SavePhysicianDemographic/';
        if(demographicModel.ethnicity == null)
            demographicModel.ethnicity = EthnicityType.NoAnswer;
        if(demographicModel.gender == null)
            demographicModel.gender = GenderType.NoAnswer;
        abpnClient.post<PhysicianDemographicModel>(url, demographicModel, authContext).then(_ => {
            setDemographicModel(_);
            setShowSuccess(true);
            setTimeout(function () {
                setShowSuccess(false);
            }, 5000);
        });
    };

    const deletePhysicianDemographic = () => {
        let url = '/api/physicianDemographic/DeletePhysicianDemographic/';
        abpnClient.post<number>(url, demographicModel.personKey, authContext).then(res => {
            var _ = new PhysicianDemographicModel();
            _.personKey = res;
            setDemographicModel(_);
            setShowDeleteSuccess(true);
            setTimeout(function () {
                setShowDeleteSuccess(false);
            }, 5000);
        });
    };

    const populateEthnicities = (() => {
        const Ethnicities = [
            {
              id: "1",
              name: "Hispanic or Latino(a)"
            },
            {
              id: "2",
              name: "Non-Hispanic or Non-Latino(a)"
            },
            {
              id: "3",
              name: "Other"
            },
            {
              id: "4",
              name: "No answer"
            }
        ];

        return (
            <Form.Group controlId="DemographicEthnicity">
                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Ethnic Identity</Form.Label>
                {Ethnicities.map((ethnicity) => {
                    return(
                        <Form.Check type="radio">
                            <Form.Check.Input type="radio" id={ethnicity.id} name="ethnicity"
                                        checked={demographicModel.ethnicity == Number.parseInt(ethnicity.id)}
                                        onChange={handleDemographicEthnicityChange} />
                            <Form.Check.Label htmlFor={ethnicity.id} className="mb-1 font-weight-bolder pp-font-size-smaller">{ethnicity.name}</Form.Check.Label>
                        </Form.Check>
                    )
                })}
            </Form.Group>
        );
    });

    const populateGenders = (() => {
        const Genders = [
            {
                id: "1",
                name: "Female"
            },
            {
                id: "2",
                name: "Male"
            },
            {
                id: "3",
                name: "Non-binary"
            },
            {
                id: "4",
                name: "Other"
            },
            {
                id: "5",
                name: "No answer"
            }
        ];

        return (
            <Form.Group controlId="DemographicGender">
                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Gender Identity</Form.Label>
                {Genders.map((gender) => {
                    return(
                        <Form.Check type="radio">
                            <Form.Check.Input type="radio" id={gender.id} name="gender"
                                        checked={demographicModel.gender == Number.parseInt(gender.id)}
                                        onChange={handleDemographicGenderChange} />
                            <Form.Check.Label htmlFor={gender.id} className="mb-1 font-weight-bolder pp-font-size-smaller">{gender.name}</Form.Check.Label>
                        </Form.Check>
                    )
                })}
            </Form.Group>
          );
    });

    const populateRaces = (() => {
        const Races = [
            {
                id: "1",
                name: "Asian"
            },
            {
                id: "2",
                name: "Black or African American"
            },
            {
                id: "3",
                name: "American Indian or Alaskan Native"
            },
            {
                id: "4",
                name: "Native Hawaiian or Pacific Islander"
            },
            {
                id: "5",
                name: "White"
            },
            {
                id: "6",
                name: "Other"
            },
            {
                id: "7",
                name: "No answer"
            }
        ];

        return (
            <Form.Group controlId="DemographicRaces">
                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Racial Identity (multiple selections allowed)</Form.Label>
                {Races.map((race) => {
                    return (
                        <Form.Check>
                            <Form.Check.Input type="checkbox" key={race.id} value={race.id} name="races" checked={isDemographicRaceChecked(race.id)} onChange={handleDemographicRaceChange} />
                            <Form.Check.Label className="font-weight-bolder mb-1 pp-font-size-smaller">{race.name}</Form.Check.Label>
                        </Form.Check>
                    );
                })}
            </Form.Group>
        );
    });

    if(!demographicModel)
    return (
        <AbpnSpinner />
    );
    return (
        <Card className="mb-3" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="font-weight-bolder" onClick={() => setOpenCollapse(!openCollapse)}>
                    <span className="text-pp-primary"><i className="font-weight-bolder las la-angle-down"></i> Demographics</span>
                </Card.Title>
                <Collapse in={openCollapse}>
                    <Row>
                        <Col xs={12} md={12}>
                            <Card>
                            {showSuccess && <Alert variant="pp-alert-success">Success! Your Demographic information has been updated.</Alert>}
                                {showDeleteSuccess && <Alert variant="pp-alert-success">Success! Your Demographic information has been deleted.</Alert>}
                                <Card.Body>
                                    <h6><strong>Submit Data for ABPN Demographic Survey</strong></h6>
                                    <p>
                                        The American Board of Psychiatry and Neurology (ABPN) and its Board of Directors are committed to advancing diversity, equity, and inclusion (DEI) within all ABPN programs and services.
                                    </p>
                                    <p>
                                        Beginning today, the ABPN will begin collecting detailed demographic data of all ABPN physicians to help enhance ABPN certification programs and the mission of the organization. When logging into the Physician Portal, you will be asked to submit information on your race, ethnicity, and gender.  Completing this demographic survey is optional for all candidates and diplomates.  Choosing to participate or not participate in the survey will have no bearing on a physician’s certification status.
                                    </p>
                                    <p>
                                        The ABPN is committed to using this demographic data to analyze diversity, equity, and inclusion within the certification process and to ensure that all physicians are treated equitably.
                                    </p>
                                    <p>
                                        The ABPN will not share data that could be used to identify individual residents, training programs, applicants, candidates, diplomates, or former diplomates.
                                    </p>      
                                    <Row>
                                        <Col xs={12} lg={4} className="px-2">
                                            {populateRaces()}
                                        </Col>
                                        <Col xs={12} lg={4} className="px-2">
                                            {populateEthnicities()}
                                        </Col>
                                        <Col xs={12} lg={4} className="px-2">
                                            {populateGenders()}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12} lg={4} className="px-2">
                                            <Button type="submit" variant="pp-primary" className="mr-4 font-weight-bold" onClick={savePhysicianDemographic}>Save</Button>
                                            <Button variant="pp-primary" hidden={demographicModel.physicianDemographicId == null} className="mr-4 font-weight-bold" onClick={deletePhysicianDemographic}>Delete</Button>
                                        </Col>
                                        <Col xs={12} lg={4} className="px-2">
                                            
                                        </Col>
                                        <Col xs={12} lg={4} className="px-2" />
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Collapse>
            </Card.Body>
        </Card>
    );
}